import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  PersonType,
  DocumentIndividualType,
  AuthToken,
  DraftUUID,
  Email,
  StepEndpoint,
  GenderInitials,
  RegenToken,
  DocumentRepresentativeType,
  DocumentBusinessType,
  CompanyType,
  AuthSignatoryToken,
  DocumentType,
  DocumentMetadata,
  PartnerCompanyType,
  CompanyPersonType,
  CreateCryptoOnboardingRequest,
  DraftBase,
} from "../types";
import { buildDraftBase, getDraftBaseFromSteps } from "../utils/draftBase";
import { updateDraftBase } from "../reducers/authReducer";
import { onUpdateInfoStep } from "../reducers/infoStepReducer";
import {
  onUpdateExpiredSessionResume,
  onUpdateStep,
} from "../reducers/onboardingDataReducer";
import {
  ConsentAcceptedLink,
  ConsentValidLink,
} from "../utils/partnerConsent";
import { UvaIndicatorResponse } from "../utils/uva";
import {
  FAL_ELECTION_URL,
  FalElectionError,
  FalFundsInfo,
} from "../utils/falFunds";
import { store } from "../app/store";
import { requestFreshToken } from "../utils/crypto";
import { setLastTraceId } from "../utils/lastTraceId";

type RetriedRequestConfig = InternalAxiosRequestConfig & {
  retriedAfterUnauthorized?: boolean;
};

const BASE_URL = import.meta.env.VITE_BASE_URL;
const BASE_URL_PITBULL = import.meta.env.VITE_BASE_URL_PITBULL;
const BASE_URL_HORUS = import.meta.env.VITE_BASE_URL_HORUS;
const company = import.meta.env.VITE_COMPANY as CompanyType;

//Creating an instance of axios to use endpoints with token
const axiosWithToken = axios.create({
  baseURL: BASE_URL,
});

axiosWithToken.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.authentication.token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Partner consent links live in each partner's own Lambda and take no
// credentials: the token in the URL is the credential. So they get bare
// instances, with none of the bearer-token interceptors below — and no custom
// headers either, since the CORS policy on those endpoints only allows
// `content-type` and anything else fails the preflight.
const consentInstances = new Map<string, AxiosInstance>();

const axiosConsent = (baseURL: string) => {
  const existing = consentInstances.get(baseURL);
  if (existing) return existing;

  const instance = axios.create({ baseURL });
  consentInstances.set(baseURL, instance);
  return instance;
};

const axiosWithTokenHorus = axios.create({
  baseURL: BASE_URL_HORUS,
});

axiosWithTokenHorus.interceptors.request.use((config) => {
  const token = store.getState().authentication.token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Only on the Horus instance, which is used exclusively by the crypto onboarding.
// Its token is injected once by the Allaria app and can expire during the flow,
// and unlike the other onboardings there is no token step to redo. So a 401 is
// answered by asking the app for a fresh token and replaying the request once.
axiosWithTokenHorus.interceptors.response.use(undefined, async (error) => {
  const config = error.config as RetriedRequestConfig | undefined;

  if (!axios.isAxiosError(error) || error.response?.status !== 401 || !config) {
    return Promise.reject(error);
  }

  // Retried at most once: if the fresh token is rejected too, the problem is not
  // the token and looping would only delay the error.
  if (config.retriedAfterUnauthorized) return Promise.reject(error);
  config.retriedAfterUnauthorized = true;

  const token = await requestFreshToken();
  if (!token) return Promise.reject(error);

  return axiosWithTokenHorus(config);
});

const captureTraceId = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const traceId = error.response?.data?.traceId;
    if (typeof traceId === "string" && traceId) setLastTraceId(traceId);
  }
  return Promise.reject(error);
};

axios.interceptors.response.use(undefined, captureTraceId);
axiosWithToken.interceptors.response.use(undefined, captureTraceId);
axiosWithTokenHorus.interceptors.response.use(undefined, captureTraceId);

export const isBlockedOnboardingError = (error: unknown) =>
  axios.isAxiosError(error) &&
  error.response?.status === 403 &&
  error.response.data?.code === "BLOCKED_ONBOARDING";

const redirectBlockedOnboarding = (error: unknown) => {
  const isSecurities =
    store.getState().activeTemplate?.onboardingType === "SECURITIES";

  if (isBlockedOnboardingError(error) && !isSecurities) {
    store.dispatch(onUpdateInfoStep("BLACKLIST"));
    store.dispatch(onUpdateStep("information-step"));
  }
  return Promise.reject(error);
};

axios.interceptors.response.use(undefined, redirectBlockedOnboarding);
axiosWithToken.interceptors.response.use(undefined, redirectBlockedOnboarding);

export const isExpiredSessionError = (error: unknown) =>
  axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  error.response.data?.code === "EXPIRED_SESSION";

const isMidgardRequest = (error: unknown) =>
  axios.isAxiosError(error) &&
  !!BASE_URL &&
  !!error.config &&
  axios.getUri(error.config).startsWith(BASE_URL);

const redirectExpiredSession = (error: unknown) => {
  const { activeTemplate, onboardingData } = store.getState();
  const isSecurities = activeTemplate?.onboardingType === "SECURITIES";

  if (
    isExpiredSessionError(error) &&
    isMidgardRequest(error) &&
    !isSecurities &&
    !onboardingData.expiredSessionResume
  ) {
    const { step, subStep } = onboardingData.stepData;
    store.dispatch(
      onUpdateExpiredSessionResume({
        step,
        subStep,
        preconfirmation: window.location.pathname === "/preconfirmacion",
      }),
    );
  }
  return Promise.reject(error);
};

axios.interceptors.response.use(undefined, redirectExpiredSession);
axiosWithToken.interceptors.response.use(undefined, redirectExpiredSession);

//AUTHENTICATION CONTROLLER
interface AuthEmailProps {
  email: string;
  companyType: CompanyType;
  es?: boolean;
  partnerCompanyType?: PartnerCompanyType;
  oldAuth?: boolean;
  cuit?: string; // REQUIRED, delete "?" when implemented in all onboardings
  referrer?: string;
  juridicalPersonCuit?: string;
}

const getAuthRequestUrl = (
  path: string,
  { companyType, partnerCompanyType, oldAuth }: AuthEmailProps,
) => {
  let url = `${BASE_URL}/auth/${path}?companyType=${companyType}`;
  if (partnerCompanyType) {
    url += `&partnerCompanyType=${partnerCompanyType}`;
  }
  if (oldAuth) {
    url += `&oldAuth=true`;
  }
  return url;
};

export const authEmail = ({
  draftBase,
  ...props
}: AuthEmailProps & { draftBase?: DraftBase }) => {
  const { email, es, cuit, referrer, juridicalPersonCuit } = props;

  return axios.post(getAuthRequestUrl("request", props), {
    email,
    ...(cuit && { cuit }),
    ...(referrer && { referrer }),
    ...(juridicalPersonCuit && { juridicalPersonCuit }),
    ...(es ? { es } : {}),
    ...draftBase,
  });
};

export const authAdditionalSignerEmail = (props: AuthEmailProps) => {
  const { email, es } = props;

  return axios.post(getAuthRequestUrl("additional-signer-request", props), {
    email,
    ...(es ? { es } : {}),
  });
};

export const authToken = ({ signatureRequestId, email, token }: AuthToken) => {
  return axios.post(
    `${BASE_URL}/auth/token`,
    {
      signatureRequestId,
      email,
    },
    { headers: { "X-Token": token } },
  );
};

export const regenerateToken = ({
  signatureRequestId,
  email,
  companyType,
  partnerCompanyType,
  oldAuth,
  es,
}: RegenToken) => {
  let url = `${BASE_URL}/auth/regenerate?companyType=${companyType}`;
  if (partnerCompanyType) {
    url += `&partnerCompanyType=${partnerCompanyType}`;
  }

  if (oldAuth) {
    url += `&oldAuth=true`;
  }

  return axios.post(url, {
    signatureRequestId,
    email,
    ...(es ? { es } : {}),
  });
};

export const authCoownerToken = ({
  signatureRequestId,
  email,
  externalCode,
  token,
}: AuthSignatoryToken) => {
  return axios.post(
    `${BASE_URL}/auth/co-owner-token`,
    {
      signatureRequestId,
      email,
      externalCode,
    },
    { headers: { "X-Token": token } },
  );
};

export const getFirebaseToken = () =>
  axiosWithToken.post<{ firebaseToken: string }>(
    `${BASE_URL}/auth/firebase-token`,
  );

export const authSignatoryToken = ({
  signatureRequestId,
  email,
  externalCode,
  token,
}: AuthSignatoryToken) => {
  return axios.post(
    `${BASE_URL}/auth/representative-token`,
    {
      signatureRequestId,
      email,
      externalCode,
    },
    { headers: { "X-Token": token } },
  );
};

//DRAFTS
//Draft uid we get from the response of the authToken (jwt).
export const getDraft = async ({ draftUUID }: { draftUUID: DraftUUID }) => {
  const response = await axiosWithToken.get(
    `${BASE_URL}/drafts/${draftUUID}`,
    {},
  );

  const { cuit, referrer, juridicalPersonCuit } = response.data ?? {};
  if (cuit) {
    store.dispatch(
      updateDraftBase(buildDraftBase({ cuit, referrer, juridicalPersonCuit })),
    );
  }

  return response;
};

export const getDrafts = ({
  cuit,
  personType,
}: {
  cuit: string;
  personType: PersonType;
}) => {
  return axiosWithToken.get(
    `${BASE_URL}/drafts?cuit=${cuit}&personType=${personType}`,
  );
};

export const getIndividualDrafts = ({
  draftUUID,
  cuit,
  documentType,
}: {
  draftUUID: DraftUUID;
  cuit: string;
  documentType: DocumentType;
}) => {
  return axiosWithToken.get(
    `${BASE_URL}/individual-drafts/${draftUUID}/owners/${cuit}/documents/${documentType}`,
  );
};

export const getSignatoryDraft = ({ cuit }: { cuit: string }) => {
  return axiosWithToken.get(
    `${BASE_URL}/drafts/representatives?juridicalCuit=${cuit}`,
  );
};

export interface CheckAlreadyRegisteredResponse {
  state: "AVAILABLE" | "IN_USE";
  message?: string;
}

export const checkAlreadyRegistered = ({
  idNumber,
}: {
  idNumber: string;
}): Promise<AxiosResponse<CheckAlreadyRegisteredResponse>> => {
  return axiosWithToken.get(`${BASE_URL}/physical-person?idNumber=${idNumber}`);
};

export interface CreateDraftProps {
  stepId: number;
  body: {
    companyType: string;
    personType: PersonType;
    email: Email;
    cuit?: string; // REQUIRED, delete "?" when implemented in all onboardings
    referrer?: string;
    juridicalPersonCuit?: string;
    steps: StepEndpoint[];
  };
}

export const sendStep = ({ stepId, body }: CreateDraftProps) => {
  const draftBase =
    store.getState().authentication.draftBase ??
    getDraftBaseFromSteps(body.steps);

  const steps = body.steps.map(
    (step) =>
      Object.fromEntries(
        Object.entries(step).filter(([key]) => key !== "validations"),
      ) as StepEndpoint,
  );

  return axiosWithToken.post(`${BASE_URL}/steps/${stepId}`, {
    ...body,
    steps,
    ...draftBase,
  });
};

export const completeDraft = ({ draftUUID }: { draftUUID: DraftUUID }) => {
  return axiosWithToken.post(`${BASE_URL}/drafts/${draftUUID}/complete`);
};

export const draftExists = ({
  cuit,
  personType,
}: {
  cuit: string;
  personType: PersonType;
}): Promise<AxiosResponse<boolean>> => {
  return axiosWithToken.get(
    `${BASE_URL}/drafts/exists?cuit=${cuit}&personType=${personType}`,
  );
};

export const deleteIndividualDraft = ({
  draftUUID,
}: {
  draftUUID: DraftUUID;
}) => {
  return axiosWithToken.delete(`${BASE_URL}/individual-drafts/${draftUUID}`);
};

export const deleteBusinessDraft = ({
  draftUUID,
}: {
  draftUUID: DraftUUID;
}) => {
  return axiosWithToken.delete(`${BASE_URL}/business-drafts/${draftUUID}`);
};

export const deleteCoownerDraft = () =>
  axiosWithToken.delete(`${BASE_URL}/drafts/cowner`);

export const sendIndividualDocument = ({
  draftUUID,
  documentType,
  file,
  cuit,
}: {
  draftUUID: DraftUUID;
  documentType: DocumentType;
  cuit: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  file: any;
}) => {
  const formdata = new FormData();
  formdata.append("file", file);

  // This will be used in future versions
  return axiosWithToken.post(
    `${BASE_URL}/individual-drafts/${draftUUID}/owners/${cuit}/documents?documentType=${documentType}`,
    formdata,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getIndividualDocuments = ({
  draftUUID,
  cuit,
}: {
  draftUUID: DraftUUID;
  cuit: string;
}): Promise<AxiosResponse<DocumentMetadata[]>> => {
  return axiosWithToken.get(
    `${BASE_URL}/individual-drafts/${draftUUID}/owners/${cuit}/documents`,
  );
};

export const deleteIndividualDocument = ({
  draftUUID,
  documentType,
  documentUUID,
  cuit,
}: {
  draftUUID: DraftUUID;
  documentType: DocumentIndividualType;
  documentUUID: string;
  cuit: string;
}) => {
  return axiosWithToken.delete(
    `${BASE_URL}/individual-drafts/${draftUUID}/owners/${cuit}/documents/${documentType}/${documentUUID}`,
  );
};

//For business documents
export const sendBusinessDocument = ({
  draftUUID,
  documentType,
  file,
}: {
  draftUUID: DraftUUID;
  documentType: DocumentBusinessType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  file: any;
}) => {
  const formdata = new FormData();
  formdata.append("file", file);
  return axiosWithToken.post(
    `${BASE_URL}/business-drafts/${draftUUID}/documents?documentType=${documentType}`,
    formdata,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getBusinessDocuments = ({
  draftUUID,
}: {
  draftUUID: DraftUUID;
}): Promise<AxiosResponse<DocumentMetadata[]>> => {
  return axiosWithToken.get(
    `${BASE_URL}/business-drafts/${draftUUID}/documents`,
  );
};

export const deleteBusinessDocument = ({
  draftUUID,
  documentType,
  documentUUID,
}: {
  draftUUID: DraftUUID;
  documentType: DocumentBusinessType;
  documentUUID: string;
}) => {
  return axiosWithToken.delete(
    `${BASE_URL}/business-drafts/${draftUUID}/documents/${documentType}/${documentUUID}`,
  );
};

//For representative documents
export const sendBusinessRepresentativeDocument = ({
  draftUUID,
  documentType,
  file,
  cuit,
}: {
  draftUUID: DraftUUID;
  documentType: DocumentRepresentativeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  file: any;
  cuit: string;
}) => {
  const formdata = new FormData();
  formdata.append("file", file);

  return axiosWithToken.post(
    `${BASE_URL}/business-drafts/${draftUUID}/representatives/${cuit}/documents?documentType=${documentType}`,
    formdata,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

//VALIDATION CONTROLLER
export const getLivenessSessionId = () => {
  return axiosWithToken.post(`${BASE_URL}/validations/liveness-session`);
};

export const detectFaceLiveness = ({
  sessionId,
  companyPersonType,
}: {
  sessionId: string;
  companyPersonType: CompanyPersonType;
}) => {
  return axiosWithToken.post<{ status: "OK" | "CHECK" | "FAILED" }>(
    `${BASE_URL}/validations/face-liveness-detection?company=${company}`,
    {
      sessionId,
      companyPersonType,
    },
  );
};

export const oldDetectFaceLiveness = ({ sessionId }: { sessionId: string }) => {
  return axiosWithToken.post(
    `${BASE_URL}/validations/face-liveness-detection?company=${company}`,
    {
      sessionId,
    },
  );
};

export const verifyFacialRecognition = ({
  number,
  gender,
  image,
}: {
  number: string;
  gender: GenderInitials;
  image: string;
}) => {
  return axiosWithToken.post(
    `${BASE_URL}/validations/facial-recognition?company=${company}`,
    {
      number,
      gender,
      image,
    },
  );
};

export type DniCaptureMethod = "SCAN" | "UPLOAD";

export const validateDni = ({
  companyPersonType,
  cuit,
  frontImage,
  backImage,
  captureMethod,
}: {
  companyPersonType: CompanyPersonType;
  cuit?: string;
  frontImage: string;
  backImage: string;
  captureMethod: DniCaptureMethod;
}) => {
  return axiosWithToken.post(
    `${BASE_URL}/validations/dni-scan?company=${company}`,
    {
      ...(cuit ? { cuit } : {}),
      frontImage,
      backImage,
      companyPersonType,
      captureMethod,
    },
  );
};

export interface DniScanValidation {
  status: "IN_PROGRESS" | "OK" | "FAILED" | "CHECK";
  errorDetails?: { code?: string; label?: string; detail?: string } | null;
}

export const getDniScanValidation = () => {
  return axiosWithToken.get<DniScanValidation>(
    `${BASE_URL}/validations/dni-scan?company=${company}`,
  );
};

export const validateRenaper = ({
  number,
  order,
  gender,
}: {
  number: string;
  order: string;
  gender: GenderInitials;
}) => {
  return axiosWithToken.post(
    `${BASE_URL}/validations/renaper?company=${company}`,
    {
      number,
      order,
      gender,
    },
  );
};

export interface RenaperValidation {
  externalCode: string;
  status: string;
  statusDetail: string;
  name: string;
  validatedAt: string;
  information: string;
  documentURL: string | null;
  entityInformation: unknown;
}

export const getRenaperData = () => {
  return axiosWithToken.get<{ validations: RenaperValidation[] }>(
    `${BASE_URL}/validations/renaper?company=${company}`,
  );
};

export const validateAfip = ({
  cuit,
  company,
}: {
  cuit: string;
  company: CompanyType;
}) => {
  return axiosWithToken.post(
    `${BASE_URL}/validations/afip?company=${company}`,
    {
      cuit,
    },
  );
};

//ACTIVITIES
export const getPhysicalActivities = () => {
  // return axios.get(`${BASE_URL}/physical-activities`);
  return axios.get(`${BASE_URL_PITBULL}/activities/physical`);
};

export const getJuridicalActivities = () => {
  return axiosWithToken.get(`${BASE_URL}/juridical-activities`);
};

// COUNTRIES

export const getCountryList = () => {
  return axios.get(`${BASE_URL_PITBULL}/countries`);
};

// PROVINCES
export const getProvinces = (countryIsoAlpha2?: string) => {
  return axios.get(
    `${BASE_URL_PITBULL}/provinces${
      countryIsoAlpha2 ? `?countryIsoAlpha2=${countryIsoAlpha2}` : ""
    }`,
  );
};

//QUESTIONS
export const getIndividualQuestions = ({
  company,
}: {
  company: CompanyType;
}) => {
  return axiosWithToken.get(
    `${BASE_URL}/individual-questions?company=${company}`,
  );
};

export const getBusinessQuestions = ({ company }: { company: CompanyType }) => {
  return axios.get(`${BASE_URL}/business-questions?company=${company}`);
};

export const getUVAIndicator = () => {
  return axiosWithToken.get<UvaIndicatorResponse>(`${BASE_URL}/indicators/UVA`);
};

export interface LastAllariaAccountResponse {
  accountId: string;
  description?: string;
  createdAt?: string;
}

export const getLastAllariaAccount = ({
  cuit,
}: {
  cuit: string;
}): Promise<AxiosResponse<LastAllariaAccountResponse>> => {
  return axiosWithToken.get(
    `${BASE_URL}/juridical-person/${cuit}/last-allaria-account`,
  );
};

//CRYPTO
// The draft is created by the Allaria app, which passes its id in the iframe's
// URL (`uuid`): every call of the identity step is made against it.
export const validateDniCrypto = ({
  draftId,
  frontImageBase64,
  backImageBase64,
}: {
  draftId: string;
  frontImageBase64: string;
  backImageBase64: string;
}) => {
  return axiosWithTokenHorus.post<{
    result: "OK" | "FAILED" | string;
    details?: string;
  }>(`/crypto/onboarding/validate-dni`, {
    draftId,
    frontImageBase64,
    backImageBase64,
  });
};

export const uploadDniCrypto = ({
  draftId,
  frontImageBase64,
  backImageBase64,
}: {
  draftId: string;
  frontImageBase64: string;
  backImageBase64: string;
}) => {
  return axiosWithTokenHorus.post(`/crypto/onboarding/upload-dni`, {
    draftId,
    frontImageBase64,
    backImageBase64,
  });
};

export const validateDniManualCrypto = ({
  draftId,
  dniNumber,
  gender,
  order,
}: {
  draftId: string;
  dniNumber: string;
  gender: string;
  order: string;
}) => {
  return axiosWithTokenHorus.post(`/crypto/onboarding/validate-dni-manual`, {
    draftId,
    dniNumber,
    gender,
    order,
  });
};

export const getCryptoLivenessSessionId = ({ draftId }: { draftId: string }) =>
  axiosWithTokenHorus.post<{ sessionId: string }>(
    `/crypto/onboarding/liveness-session`,
    { draftId },
  );

export const validateCryptoFace = ({
  draftId,
  sessionId,
}: {
  draftId: string;
  sessionId: string;
}) =>
  axiosWithTokenHorus.post<{ result: string; error_code?: string }>(
    `/crypto/onboarding/validate-face`,
    { draftId, sessionId },
  );

// The account data the last step collects. Sent before completing.
export const createCryptoOnboarding = (body: CreateCryptoOnboardingRequest) =>
  axiosWithTokenHorus.post(`/crypto/onboarding`, body);

export const completeCryptoOnboarding = ({
  accountId,
}: {
  accountId: number;
}) =>
  axiosWithTokenHorus.post<{
    state: "PENDING" | "COMPLETED" | "MANUAL_REJECTED";
    externalCode?: string;
  }>(`/crypto/onboarding/complete`, { accountId });

//PARTNER CONSENT T&C CONTROLLER
export const validateConsentLink = ({
  baseUrl,
  token,
}: {
  baseUrl: string;
  token: string;
}) => axiosConsent(baseUrl).get<ConsentValidLink>(`/tyc/links/${token}`);

export const acceptConsentLink = ({
  baseUrl,
  token,
  documentVersion,
  accountNumber,
}: {
  baseUrl: string;
  token: string;
  /**
   * Identifies the document the person accepted — the terms or the linking
   * conformity, whichever the link's `kind` called for. Stored verbatim.
   */
  documentVersion: string;
  /**
   * Required on linking: the `numero` of the comitente picked. Anything that
   * isn't one of the person's comitentes comes back as a 422.
   */
  accountNumber?: string;
}) =>
  axiosConsent(baseUrl).post<ConsentAcceptedLink>(
    `/tyc/links/${token}/accept`,
    { document_version: documentVersion, account_number: accountNumber },
  );

// Midgard, not a partner Lambda, and public: the link's token rides in
// `X-Token` instead of a bearer, so no interceptors.
export const acceptAccountRequestNotification = ({
  uuid,
  token,
}: {
  uuid: string;
  token: string;
}) =>
  axios.post(`${BASE_URL}/account-request-notifications/${uuid}/accept`, null, {
    headers: { "X-Token": token },
  });

// Apps Script answers 200 with `ok: false` on errors, and doesn't answer a CORS
// preflight: the body goes as text/plain to stay a simple request.
const postFalElection = <T>(body: Record<string, string>) =>
  axios.post<T | FalElectionError>(FAL_ELECTION_URL, JSON.stringify(body), {
    headers: { "Content-Type": "text/plain;charset=utf-8" },
  });

export const getFalFunds = () =>
  postFalElection<FalFundsInfo & { ok: true }>({ accion: "fondos" });

export const registerFalRequest = (body: {
  uuid: string;
  razonSocial: string;
  comitente: string;
  mail: string;
}) => postFalElection<{ ok: true }>({ accion: "registrar", ...body });

export const chooseFalFund = (body: {
  uuid: string;
  cuotapartista: string;
  fondo: string;
}) => postFalElection<{ ok: true; fecha: string }>({ accion: "elegir", ...body });
